/**
 * SendCommunityImageMessageUseCase
 *
 * Upload-then-write-then-transcode hop for a single community-wall IMAGE
 * message. This is the community branch of Flutter's
 * `MediasRepository.uploadMedia` (lib/data/repository/medias_repository.dart:258-281),
 * whose two storage halves live in `AppStorageService._uploadCommunityMessageMedia`
 * (lib/services/app_storage_service.dart:216-265). The optimistic entity is built
 * upstream by `CommunityChatMessageRemapper.fromInput` (Task 2) and inserted into
 * the list by the hook BEFORE this runs — so, like `AddCommunityMessageUseCase`,
 * this use case RECEIVES the already-built entity rather than building it.
 *
 * Ordering (Flutter parity, with one sanctioned reorder):
 *   1. `await` the raw upload via `CommunityService.uploadCommunityMedia` and
 *      capture `{ uploaded, uploadPath, fileExtension }` — the storage-upload
 *      half of `_uploadCommunityMessageMedia` (app_storage_service.dart:238-248;
 *      the owner is `message.messageUsersInfo.owner`, medias_repository.dart:262).
 *   2. `await` the message doc write via `CommunityService.addMessage`
 *      UNCONDITIONALLY, regardless of `uploaded` — Flutter's doc write at
 *      medias_repository.dart:279 runs after the upload attempt regardless of the
 *      returned bool, so a failed-upload message stays rendered as "Processing"
 *      (matching the existing image-failure divergence note in `useCommunity`).
 *   3. When `uploaded`, fire the transcode Cloud Function via
 *      `CommunityService.transcodeCommunityImage` (fire-and-forget; internally
 *      un-awaited). This is gated on upload success exactly as Flutter's
 *      catch-before-fire gates it (app_storage_service.dart:249-264: the `catch`
 *      returns `false` before the callable at :250 can fire). `aspectRatio` is
 *      read off the message's OWN `mediaInfo` so the persisted doc value and the
 *      CF-echoed `mediaInfo.aspectRatio` can never diverge; `messageId` is the
 *      message's own `id` (== the Firestore doc id — community_data_source.dart:139
 *      `.doc(message.id)`, and the CF queries `where('id','==',messageId)`).
 *   4. Return the REAL `uploaded` bool (medias_repository.dart:281).
 *
 * Prompt-directed ordering deviation (task-prompt line 19): the web writes the
 * message doc (step 2) BEFORE firing the callable (step 3). The CF locates the
 * doc via
 * `collectionGroup('community_messages').where('id','==',messageId).orderBy('createdTimestamp','desc').limit(1)`
 * (transcode_community_message_image.ts:156-166), so the doc must exist before
 * the call. Flutter fires the callable before its doc write
 * (app_storage_service.dart:250 runs before medias_repository.dart:279) and only
 * wins the race because server transcoding takes seconds. This is the one
 * sanctioned side-effect reordering in the story.
 *
 * Flutter bug NOT ported (task-plan line 14; conventions.md § Handling obvious
 * Flutter bugs): `saveCommunityMedia` discards `_uploadCommunityMessageMedia`'s
 * boolean and returns `true` unconditionally
 * (app_storage_service.dart:268-294: `await _uploadCommunityMessageMedia(...);
 * return true;`), so a failed community upload still reports success and the
 * `PreviewContentScreen` failure toast never fires for community (the chat
 * sibling propagates properly, app_storage_service.dart:164-214). The web
 * returns the REAL upload result here; the one-line Flutter fix is Task 10.
 *
 * Pure-domain contract (conventions.md § Clean Architecture flow): the use case
 * calls only `CommunityService`; it does NOT touch `firestoreService` /
 * `cloudStorageService` / `cloudFunctionsService` directly (the service
 * orchestrates those) and does NOT dispatch to Redux (a presentation concern).
 * Community messages are NOT end-to-end encrypted (no encrypt step).
 */

import { CommunityService } from '@/domain/services/community/communityService';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface SendCommunityImageMessageArgs {
  /** The image file to upload. */
  file: File;
  /**
   * The ALREADY-BUILT optimistic entity from
   * `CommunityChatMessageRemapper.fromInput` (Task 2). The hook builds + inserts
   * it into the list before calling this (mirrors `AddCommunityMessageUseCase`
   * receiving the entity). Its `messageUsersInfo.owner` selects the storage +
   * Firestore write target, its `id` is the doc id + transcode target, and its
   * `mediaInfo.aspectRatio` is forwarded to the transcode payload.
   */
  message: CommunityChatMessageEntity;
}

export class SendCommunityImageMessageUseCase {
  private communityService: CommunityService;

  constructor(communityService: CommunityService = CommunityService.getInstance()) {
    this.communityService = communityService;
  }

  /**
   * Upload the image, write the message doc unconditionally, then (on upload
   * success) fire the transcode CF. Returns the REAL upload success boolean.
   *
   * @returns `true`/`false` from `CommunityService.uploadCommunityMedia` (the
   *   message doc is written either way — see step 2 in the class JSDoc), or
   *   `false` on a thrown failure.
   */
  async execute(args: SendCommunityImageMessageArgs): Promise<boolean> {
    const { file, message } = args;

    try {
      // (1) Raw upload first; owner comes from the message
      // (medias_repository.dart:262). Captures the path/extension the transcode
      // payload needs plus the real `uploaded` bool.
      const { uploaded, uploadPath, fileExtension } =
        await this.communityService.uploadCommunityMedia({
          file,
          communityOwner: message.messageUsersInfo.owner,
        });

      // (2) Write the message doc UNCONDITIONALLY (medias_repository.dart:279
      // runs after the upload attempt regardless of the `uploaded` bool).
      await this.communityService.addMessage(message);

      // (3) On upload success only, fire the transcode CF AFTER the doc write
      // (prompt line 19 — the CF locates the doc via `where('id','==',messageId)`
      // so it must exist first; the fire is internally un-awaited). Gated on
      // upload success exactly as Flutter's catch-before-fire gates it
      // (app_storage_service.dart:249-264). `aspectRatio` is read off the
      // message's own `mediaInfo` so the doc value and the CF-echoed value can
      // never diverge.
      if (uploaded) {
        this.communityService.transcodeCommunityImage({
          uploadPath,
          fileExtension,
          aspectRatio: message.mediaInfo?.aspectRatio ?? null,
          messageId: message.id,
        });
      }

      // (4) Return the REAL upload result (medias_repository.dart:281; the
      // Flutter `return true` bug is NOT ported — see class JSDoc, Task 10).
      return uploaded;
    } catch (error) {
      appLogger.error(
        'Failed to send community image message',
        'SendCommunityImageMessageUseCase.execute',
        { error: error as Error },
      );
      return false;
    }
  }
}

// Export singleton instance for convenience (matches the other community use cases).
export const sendCommunityImageMessageUseCase = new SendCommunityImageMessageUseCase();
